/**
 * Life4Earth V2 — React hook for loading curve metadata from the API.
 *
 * Replaces: localStorage reads from l4e_progress_curve_meta_v2 / l4e_finance_cost_meta_v1
 * Returns curve meta entries grouped by category.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchAllCurveMeta,
  curveMetaToStoreShape,
} from "./curveMeta";
import type { CurveMetaResponse } from "./curveMeta";
import { fetchForKey, CACHE_UPDATE_EVENT, QUERY_KEYS } from "./queryCache";

export interface UseApiCurveMetaResult {
  /** All curve meta entries from the API */
  raw: CurveMetaResponse[];
  /** Schedule curve meta keyed by "PROJECT_GUID::INSTANCE_GUID" */
  scheduleMeta: Record<string, Record<string, any>>;
  /** Finance curve meta keyed by "PROJECT_GUID::INSTANCE_GUID" */
  budgetMeta: Record<string, Record<string, any>>;
  loading: boolean;
  error: string | null;
  source: "api" | "seed" | "loading";
  refetch: () => void;
}

export function useApiCurveMeta(): UseApiCurveMetaResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiCurveMetaResult>({
    raw: [],
    scheduleMeta: {},
    budgetMeta: {},
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      const cached = await fetchForKey(
        QUERY_KEYS.curveMeta,
        async () => {
          const apiItems = await fetchAllCurveMeta();
          const sMeta: Record<string, Record<string, any>> = {};
          const fMeta: Record<string, Record<string, any>> = {};

          for (const item of apiItems) {
            const { key, category, meta } = curveMetaToStoreShape(item);
            if (category === "schedule") {
              sMeta[key] = meta;
            } else if (category === "finance") {
              fMeta[key] = meta;
            }
          }

          return {
            raw: apiItems,
            scheduleMeta: sMeta,
            budgetMeta: fMeta,
          };
        },
        { force: true }
      );

      if (mountedRef.current) {
        setData({
          raw: cached.raw,
          scheduleMeta: cached.scheduleMeta,
          budgetMeta: cached.budgetMeta,
          loading: false,
          error: null,
          source: "api",
          refetch,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setData({
          raw: [],
          scheduleMeta: {},
          budgetMeta: {},
          loading: false,
          error: err.message,
          source: "seed",
          refetch,
        });
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    async function load() {
      try {
        const cached = await fetchForKey(
          QUERY_KEYS.curveMeta,
          async () => {
            const apiItems = await fetchAllCurveMeta();
            const sMeta: Record<string, Record<string, any>> = {};
            const fMeta: Record<string, Record<string, any>> = {};

            for (const item of apiItems) {
              const { key, category, meta } = curveMetaToStoreShape(item);
              if (category === "schedule") {
                sMeta[key] = meta;
              } else if (category === "finance") {
                fMeta[key] = meta;
              }
            }

            return {
              raw: apiItems,
              scheduleMeta: sMeta,
              budgetMeta: fMeta,
            };
          }
        );

        if (mountedRef.current) {
          setData({
            raw: cached.raw,
            scheduleMeta: cached.scheduleMeta,
            budgetMeta: cached.budgetMeta,
            loading: false,
            error: null,
            source: "api",
            refetch,
          });
        }
      } catch (err: any) {
        if (mountedRef.current) {
          setData({
            raw: [],
            scheduleMeta: {},
            budgetMeta: {},
            loading: false,
            error: err.message,
            source: "seed",
            refetch,
          });
        }
      }
    }

    load();

    const handleCacheUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.key === QUERY_KEYS.curveMeta) {
        load();
      }
    };

    window.addEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, handleCacheUpdate);
    };
  }, [refetch]);

  return data;
}
